import { useState } from 'react';

const footerLinks = {
  Services: ['White-Glove Courier', 'Armed Transport', 'Vault & Insurance', 'Custom Crating', 'Temperature Control'],
  Company: ['About Us', 'Leadership', 'Careers', 'Press', 'Partners'],
  Resources: ['Track Shipment', 'Coverage Map', 'Insurance Guide', 'Compliance', 'FAQ'],
  Legal: ['Terms of Service', 'Privacy Policy', 'Insurance Terms', 'Licensing', 'Accessibility'],
};

export default function Footer() {
  const [email, setEmail] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const hpValue = (formData.get('company_alt') as string || '').trim();
    if (hpValue) {
      setFormStatus('success');
      setFormMessage('Thank you for subscribing.');
      setEmail('');
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d96lh2fpg5pqhofcuqd0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: formData.get('email') as string }).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('Thank you for subscribing.');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || 'Something went wrong.';
        setFormStatus('error');
        setFormMessage(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('Network error. Please try again.');
    }
  };

  return (
    <footer className="relative bg-primary-950 text-white overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none">
        <svg viewBox="0 0 1440 600" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
          <path d="M0 200 Q200 150 400 220 T800 180 T1200 210 T1440 190" stroke="white" strokeWidth="0.5" fill="none"/>
          <path d="M0 250 Q250 210 500 270 T900 230 T1300 260 T1440 240" stroke="white" strokeWidth="0.5" fill="none"/>
          <path d="M0 300 Q300 260 550 320 T950 280 T1350 310 T1440 290" stroke="white" strokeWidth="0.5" fill="none"/>
          <path d="M0 350 Q200 310 450 370 T850 330 T1250 360 T1440 340" stroke="white" strokeWidth="0.5" fill="none"/>
          <path d="M0 400 Q250 360 500 420 T900 380 T1300 410 T1440 390" stroke="white" strokeWidth="0.5" fill="none"/>
          <path d="M100 150 Q300 120 500 170 T800 140 T1100 165 T1300 155" stroke="white" strokeWidth="0.5" fill="none"/>
          <path d="M200 450 Q400 420 600 470 T900 440 T1200 465 T1400 455" stroke="white" strokeWidth="0.5" fill="none"/>
        </svg>
      </div>

      <div className="relative z-10 px-6 md:px-10 lg:px-16 py-16 md:py-20">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 mb-16 md:mb-20">
            <div className="lg:w-[35%]">
              <a href="/" className="inline-block cursor-pointer mb-6">
                <span className="font-heading text-2xl md:text-3xl font-bold tracking-wider text-white">
                  INTL<span className="text-accent-500">.</span>
                </span>
              </a>

              <a href="tel:+18888663428" className="inline-flex items-center gap-2 text-white/60 hover:text-white/90 transition-colors duration-300 mb-5 cursor-pointer group">
                <i className="ri-phone-line text-accent-500 text-lg group-hover:text-accent-400 transition-colors duration-300"></i>
                <span className="text-base font-medium tracking-wide">888-866-3428</span>
              </a>

              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex items-center gap-2 mb-6"
              >
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-hp-input"
                />
                <div className="relative flex-1">
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email address"
                    required
                    className="w-full bg-transparent border border-white/20 rounded-full px-5 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-accent-500 transition-colors duration-300"
                  />
                </div>
                <button
                  type="submit"
                  className="w-11 h-11 flex items-center justify-center rounded-full bg-accent-500 hover:bg-accent-400 transition-colors duration-300 flex-shrink-0 cursor-pointer"
                >
                  <i className="ri-notification-3-line text-white text-lg"></i>
                </button>
              </form>

              {formStatus !== 'idle' && (
                <p className={`text-xs mb-5 ${formStatus === 'success' ? 'text-accent-400' : 'text-red-400'}`}>
                  {formMessage}
                </p>
              )}

              <p className="font-heading text-xl md:text-2xl text-white/90 leading-snug">
                The world&rsquo;s most valuable<br />
                shipments, delivered with<br />
                absolute certainty.
              </p>
            </div>

            <div className="lg:w-[65%] grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-6">
              {Object.entries(footerLinks).map(([heading, links]) => (
                <div key={heading}>
                  <h4 className="text-white text-sm font-semibold tracking-wide mb-4 uppercase">{heading}</h4>
                  <ul className="flex flex-col gap-2.5">
                    {links.map((link) => (
                      <li key={link}>
                        <a href="#" className="text-white/50 text-sm hover:text-white/80 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                          {link}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-6">
            <p className="text-white/40 text-xs order-2 sm:order-1">
              &copy; {new Date().getFullYear()} INTL Couriers. All rights reserved.
            </p>

            <div className="flex items-center gap-4 order-1 sm:order-2">
              {['ri-linkedin-fill', 'ri-twitter-x-fill', 'ri-instagram-line', 'ri-youtube-fill'].map((icon) => (
                <a
                  key={icon}
                  href="#"
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-white/20 text-white/60 hover:text-white hover:border-white/40 transition-all duration-300 cursor-pointer"
                >
                  <i className={`${icon} text-sm`}></i>
                </a>
              ))}
            </div>

            <a href="#" className="text-white/40 text-xs hover:text-white/70 transition-colors duration-200 order-3 whitespace-nowrap cursor-pointer">
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}